<template>
    <router-view v-slot="{ Component }">
        <component
            v-if="shouldShowComponent"
            :class="{ blur: uiStore.showPageLoader }"
            :is="Component"
        />
    </router-view>
    <transition name="fade" mode="in-out">
        <brand-loader v-if="uiStore.showPageLoader" />
    </transition>
    <enact-load-error v-if="showEnactLoadError" />
    <no-access-helper
        v-if="shouldShowNoAccess"
        :resource="currentPageName"
        :no-access-message="noAccessMessage"
        :is-old-enact-page="isOldEnactPage"
    />
</template>

<script setup lang="ts">
import { useUIStore } from '@/stores/uiStore.ts';
import {
    computed,
} from 'vue';
import BrandLoader from '@/components/layout/BrandLoader.vue';
import EnactLoadError from '@/components/layout/EnactLoadError.vue';
import NoAccessHelper from '@/components/layout/NoAccessHelper.vue';
import { useRoute } from 'vue-router';
import { useDataStore } from '@/stores/powerMarkets/dataStore.ts';
import { enactRoutes } from './routes/enactRoutes.ts';
import { useUserAccessStore } from './stores/userAccessStore.ts';

const props = withDefaults(defineProps<({
    enactInitialised?: boolean;
    initialised?: boolean;
    showNoAccess?: boolean;
})>(), {
    enactInitialised: true,
    initialised: true,
    showNoAccess: true,
});

const uiStore = useUIStore();
const dataStore = useDataStore();
const userAccessStore = useUserAccessStore();
const route = useRoute();

const currentRouteIsEnact = computed<boolean>(() => !!route.matched.find(r => r.meta.isEnact));
const isInitialised = computed<boolean>(() => props.initialised && (currentRouteIsEnact.value ? props.enactInitialised : true));
const currentPageName = computed(() => (route.meta.headName ? `${route.meta.headName}` : 'this page'));
const isOldEnactPage = computed<boolean>(() => route.fullPath.includes(enactRoutes.benchmarks.path) || route.fullPath.includes(enactRoutes.markets.path));
const hasEnactAccess = computed<boolean>(() => !!userAccessStore.userAccess.enact.shortTermMarkets);
const shouldShowComponent = computed(() => isInitialised.value && !showEnactLoadError.value && (accessToView.value || !props.showNoAccess));
const shouldShowNoAccess = computed(() => isInitialised.value && !showEnactLoadError.value && !accessToView.value && props.showNoAccess);

// This is to stop Enact users being able to use Enact pages if the static store API call fails, as this basically means they shouldn't be able to do anything on Enact, and if we let them try and use the pages it could mess up their user settings in the DB
const showEnactLoadError = computed<boolean>(() => currentRouteIsEnact.value
    && props.initialised
    && props.enactInitialised
    && hasEnactAccess.value
    && dataStore.staticStoreFetchFailed);

const accessToView = computed<boolean>(() => {
    const accessName = route.meta?.accessName;
    if (!accessName) return true; // TODO: sort this true. What to do about rerouting and hidden pages etc? Do we really want to have to add them to this object? e.g. single chart, mute data check
    if (accessName === 'admin') {
        const adminAccess = userAccessStore.enactPageAccess.admin.hasAccess;
        const flexBenchmarkAdmin = userAccessStore.flexIndexAccess?.isAdmin ?? false;
        return adminAccess || flexBenchmarkAdmin;
    }
    const accessObject = userAccessStore.enactPageAccess[accessName];
    return accessObject ? accessObject.hasAccess : false;
});

const noAccessMessage = computed<string | undefined>(() => {
    const accessName = route.meta?.accessName;
    if (!accessName) return undefined;
    const accessObject = userAccessStore.enactPageAccess[accessName];
    return accessObject ? accessObject?.noAccessMessage : '';
});

</script>
